import { FC, useState, useCallback, useContext, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/modal';
import { useNotification } from '@/components/notifications';
import { updateCollection } from '@/services/data-sources/update-collection';
import { useLaunchDarkly } from '@/providers/launchdarkly-provider';
import { Collection, CollectionSource } from '@/types/source-types';
import { CollectionForm, validateCollectionName } from './collection-form';
import { CollectionSourceSection } from './collection-source-section';
import { ExposedUploadAttributesSection } from './exposed-upload-attributes-section';
import { useGetCollectionInfo } from '@/hooks/queries/data-sources/use-get-collection-info';
import { useOrgStore } from '@/stores/org-store';
import type { WordPressSiteFormErrors } from './wordpress-site-form';
import type { WordPressSiteFormState } from './wordpress-site-form';
import {
  DEFAULT_WORDPRESS_SCHEDULE,
  inferCollectionSource,
  createEmptySiteState,
  mapCollectionToSitesState,
  validateWordPressSites,
  buildSitesPayload,
} from './wordpress-source-utils';
import { AuthContext } from '@/context/auth-context';

interface EditCollectionModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  collection: Collection | null;
  collectionId: string | null;
  onCollectionUpdated: (updatedCollection: Collection | null) => void;
  collections?: Collection[];
}

export const EditCollectionModal: FC<EditCollectionModalProps> = ({
  isOpen,
  onOpenChange,
  collection,
  collectionId,
  onCollectionUpdated,
  collections = [],
}) => {
  const notification = useNotification();
  const { isAdmin } = useContext(AuthContext);
  const { isCollectionSourceEnabled } = useLaunchDarkly();
  const [editedName, setEditedName] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [hasError, setHasError] = useState('');
  const [collectionSource, setCollectionSource] =
    useState<CollectionSource>('uploads');
  const [wordpressSites, setWordpressSites] = useState<
    WordPressSiteFormState[]
  >([createEmptySiteState()]);
  const [siteErrors, setSiteErrors] = useState<
    Record<string, WordPressSiteFormErrors>
  >({});
  const [expandedSiteId, setExpandedSiteId] = useState<string | null>(null);
  const [reconcileEnabled, setReconcileEnabled] = useState(false);
  const [exposedAttributes, setExposedAttributes] = useState<string[]>([]);
  const [exposedCardinality, setExposedCardinality] = useState<
    Record<string, 'single' | 'multi'>
  >({});
  const isUnstructuredCollection = collection?.data_type !== 'structured';
  const orgId = useOrgStore((state) => state.currentOrg?.orgid);

  const isQdrantCollection =
    isUnstructuredCollection && collection?.destination === 'qdrant';

  const {
    data: collectionInfo,
    isLoading: isCollectionInfoLoading,
    isError: isCollectionInfoError,
  } = useGetCollectionInfo({
    collectionId: isQdrantCollection ? collectionId || undefined : undefined,
    orgId,
    enabled: isOpen && isQdrantCollection,
  });

  // Handle opening edit modal
  const handleOpenModal = useCallback(() => {
    setEditedName(collection?.name || '');
    setEditedDescription(collection?.description || '');

    const nextSource = inferCollectionSource(collection);
    setCollectionSource(nextSource);

    setReconcileEnabled(collection?.source_config?.reconcile_enabled ?? false);

    if (nextSource === 'wordpress_api') {
      const sites = mapCollectionToSitesState(collection);
      const initialSites = sites.length > 0 ? sites : [createEmptySiteState()];
      setWordpressSites(initialSites);
      setExpandedSiteId(initialSites[0].siteId);
    } else {
      const emptySite = createEmptySiteState();
      setWordpressSites([emptySite]);
      setExpandedSiteId(emptySite.siteId);
    }

    setSiteErrors({});
    setExposedAttributes(collection?.exposed_upload_attributes ?? []);
    setExposedCardinality(
      collection?.exposed_upload_attribute_cardinality ?? {},
    );
  }, [collection]);

  // Handle closing edit modal
  const handleCloseModal = useCallback(() => {
    onOpenChange(false);
    setEditedName('');
    setEditedDescription('');
    setCollectionSource('uploads');
    const emptySite = createEmptySiteState();
    setWordpressSites([emptySite]);
    setExpandedSiteId(emptySite.siteId);
    setSiteErrors({});
    setExposedAttributes([]);
    setExposedCardinality({});
    setReconcileEnabled(false);
  }, [onOpenChange]);

  // Handle saving collection changes
  const handleSaveCollection = useCallback(async () => {
    if (!collection || !collectionId) return;

    const trimmedName = editedName.trim();
    const trimmedDescription = editedDescription.trim();
    const currentDescription = collection.description || '';

    const validationError = validateCollectionName(editedName);
    if (validationError) {
      setHasError(validationError);
      return;
    }

    const nameChanged = trimmedName !== collection.name;

    if (nameChanged) {
      const isDuplicateName = collections.some(
        (c) =>
          c.id !== collectionId &&
          c.name.trim().toLowerCase() === trimmedName.toLowerCase(),
      );
      if (isDuplicateName) {
        setHasError('Collection name already exists');
        return;
      }
    }

    setHasError('');

    const currentSource = inferCollectionSource(collection);
    const selectedSource: CollectionSource = isUnstructuredCollection
      ? collectionSource
      : 'uploads';
    const descriptionChanged = trimmedDescription !== currentDescription;
    const sourceChanged = isUnstructuredCollection
      ? currentSource !== selectedSource
      : false;

    let sourceConfigPayload: Collection['source_config'] | null | undefined;
    let configChanged = false;

    // When the collection already has WordPress sites, the modal no longer
    // manages them (site config + reconcile + add-site live on the collection
    // page). Never rebuild source_config from the modal's stale site state —
    // that could wipe or clobber per-site credentials. Leave source_config
    // untouched and only persist collection-level fields (name/description).
    const wpManagedOnPage =
      inferCollectionSource(collection) === 'wordpress_api' &&
      (collection.source_config?.wordpress_sites?.length ?? 0) > 0;

    if (
      isUnstructuredCollection &&
      selectedSource === 'wordpress_api' &&
      wpManagedOnPage
    ) {
      setSiteErrors({});
      sourceConfigPayload = undefined;
    } else if (isUnstructuredCollection && selectedSource === 'wordpress_api') {
      // Validate all sites
      const errors = validateWordPressSites(wordpressSites, {
        isEditing: true,
      });

      setSiteErrors(errors);

      if (Object.keys(errors).length > 0) {
        // Auto-expand the first errored site
        const firstErrorSiteId = Object.keys(errors)[0];
        setExpandedSiteId(firstErrorSiteId);
        return;
      }

      // Build the payload, merging with existing source_config to preserve
      // fields the UI doesn't manage (e.g. wordpress_exclusion_rules)
      const existingConfig = collection.source_config;
      // Only persist reconcile_enabled when it carries meaning: the user
      // turned it on, or the collection already stored the flag. Writing a
      // default `false` onto a legacy config that never had the key would
      // dirty the JSON comparison below and trigger a spurious source_config
      // rewrite on unrelated edits (name/description), which can drop stored
      // credential markers from existing sites.
      const includeReconcile =
        reconcileEnabled || existingConfig?.reconcile_enabled !== undefined;
      const payload = {
        ...(existingConfig ?? {}),
        ...buildSitesPayload(wordpressSites),
        ...(includeReconcile ? { reconcile_enabled: reconcileEnabled } : {}),
      };

      // Check if anything actually changed
      const newConfigJson = JSON.stringify(payload);
      const existingConfigJson = existingConfig
        ? JSON.stringify(existingConfig)
        : '';

      configChanged = sourceChanged || newConfigJson !== existingConfigJson;

      if (configChanged) {
        sourceConfigPayload = payload;
      } else {
        sourceConfigPayload = undefined;
      }
    } else {
      setSiteErrors({});
      if (
        isUnstructuredCollection &&
        currentSource === 'wordpress_api' &&
        selectedSource === 'uploads'
      ) {
        configChanged = true;
        sourceConfigPayload = null;
      } else {
        sourceConfigPayload = undefined;
      }
    }

    const currentAttrs = collection.exposed_upload_attributes ?? [];
    const sortedCurrentAttrs = [...currentAttrs].sort();
    const sortedNextAttrs = [...exposedAttributes].sort();
    const attrsChanged =
      isQdrantCollection &&
      JSON.stringify(sortedCurrentAttrs) !== JSON.stringify(sortedNextAttrs);

    const selectedSet = new Set(exposedAttributes);
    const prunedCardinality = Object.fromEntries(
      Object.entries(exposedCardinality).filter(([k]) => selectedSet.has(k)),
    );
    const currentCardinality =
      collection.exposed_upload_attribute_cardinality ?? {};
    const sortedKeys = (obj: Record<string, string>) => Object.keys(obj).sort();
    const cardinalityChanged =
      isQdrantCollection &&
      (JSON.stringify(sortedKeys(currentCardinality)) !==
        JSON.stringify(sortedKeys(prunedCardinality)) ||
        sortedKeys(prunedCardinality).some(
          (k) => currentCardinality[k] !== prunedCardinality[k],
        ));

    if (
      !nameChanged &&
      !descriptionChanged &&
      !sourceChanged &&
      !configChanged &&
      !attrsChanged &&
      !cardinalityChanged
    ) {
      handleCloseModal();
      return;
    }

    try {
      setIsUpdating(true);

      const shouldSendSourceFields =
        isUnstructuredCollection &&
        (sourceChanged || sourceConfigPayload !== undefined);

      const response = await updateCollection({
        id: collectionId,
        ...(nameChanged ? { name: trimmedName } : {}),
        ...(descriptionChanged ? { description: trimmedDescription } : {}),
        ...(shouldSendSourceFields
          ? {
              source: selectedSource,
              sourceConfig: sourceConfigPayload,
            }
          : {}),
        ...(attrsChanged ? { exposedUploadAttributes: exposedAttributes } : {}),
        ...(cardinalityChanged
          ? { exposedUploadAttributeCardinality: prunedCardinality }
          : {}),
      });

      if (response?.detail) {
        setHasError(response.detail);
        return;
      }

      // Build the optimistic updated collection state
      let nextSourceConfig: Collection['source_config'] | null | undefined;
      if (shouldSendSourceFields) {
        if (sourceConfigPayload === null) {
          nextSourceConfig = null;
        } else if (sourceConfigPayload !== undefined) {
          // Strip passwords from the local state
          const payload = sourceConfigPayload;
          if (payload?.wordpress_sites) {
            nextSourceConfig = {
              ...payload,
              wordpress_sites: payload.wordpress_sites.map((site) => ({
                ...site,
                auth: site.auth
                  ? {
                      username: site.auth.username,
                      has_credentials:
                        !!site.auth.password || site.auth.has_credentials,
                    }
                  : undefined,
              })),
            };
          } else {
            nextSourceConfig = payload;
          }
        } else {
          nextSourceConfig = collection.source_config;
        }
      } else {
        nextSourceConfig = collection.source_config;
      }

      const updatedCollection = collection
        ? {
            ...collection,
            ...(nameChanged ? { name: trimmedName } : {}),
            ...(descriptionChanged ? { description: trimmedDescription } : {}),
            ...(shouldSendSourceFields
              ? {
                  source: selectedSource,
                  source_config: nextSourceConfig ?? null,
                }
              : {}),
            ...(attrsChanged
              ? { exposed_upload_attributes: exposedAttributes }
              : {}),
            ...(cardinalityChanged
              ? { exposed_upload_attribute_cardinality: prunedCardinality }
              : {}),
          }
        : null;

      onCollectionUpdated(updatedCollection);
      notification.success('Collection updated successfully');
      handleCloseModal();
    } catch (error) {
      console.error('Error updating collection:', error);
      notification.error('Failed to update collection');
    } finally {
      setIsUpdating(false);
    }
  }, [
    collection,
    collectionId,
    collections,
    collectionSource,
    editedName,
    editedDescription,
    exposedAttributes,
    exposedCardinality,
    handleCloseModal,
    isQdrantCollection,
    isUnstructuredCollection,
    notification,
    onCollectionUpdated,
    wordpressSites,
    reconcileEnabled,
  ]);

  // Initialize form when modal opens (guard against null collection during close)
  useEffect(() => {
    if (isOpen && collection) {
      handleOpenModal();
    }
  }, [isOpen, collection, handleOpenModal]);

  // A collection that already stores WordPress sites manages them inline on the
  // collection page. The modal then hides site management and locks the source
  // radio: switching to uploads sets source_config = null, which would wipe all
  // sites and their stored credentials in one save. Guard against that footgun.
  const hasExistingWpSites =
    inferCollectionSource(collection) === 'wordpress_api' &&
    (collection?.source_config?.wordpress_sites?.length ?? 0) > 0;

  const footer = (
    <div className='flex gap-2'>
      <Button
        variant='outline'
        onClick={handleCloseModal}
        disabled={isUpdating}
      >
        Cancel
      </Button>
      <Button onClick={handleSaveCollection} disabled={isUpdating}>
        {isUpdating ? 'Saving...' : 'Save Changes'}
      </Button>
    </div>
  );

  return (
    <Modal
      title='Edit Collection'
      isDialogOpen={isOpen}
      setIsDialogOpen={onOpenChange}
      footer={footer}
    >
      <CollectionForm
        name={editedName}
        setName={setEditedName}
        description={editedDescription}
        setDescription={setEditedDescription}
        hasError={hasError}
        setHasError={setHasError}
        isLoading={isUpdating}
        onSubmit={handleSaveCollection}
        nameLabel='Collection Name'
      />
      {isQdrantCollection && isAdmin && (
        <div className='mt-6'>
          <ExposedUploadAttributesSection
            payloadSchema={collectionInfo?.payload_schema}
            selected={exposedAttributes}
            onChange={setExposedAttributes}
            cardinality={exposedCardinality}
            onCardinalityChange={setExposedCardinality}
            isLoading={isCollectionInfoLoading}
            isError={isCollectionInfoError}
            disabled={isUpdating}
          />
        </div>
      )}
      {isUnstructuredCollection && (isCollectionSourceEnabled || isAdmin) && (
        <div className='mt-6'>
          <CollectionSourceSection
            source={collectionSource}
            onSourceChange={(next) => {
              setCollectionSource(next);
              if (next === 'wordpress_api') {
                // Ensure sites have a default schedule
                setWordpressSites((prev) =>
                  prev.map((site) => ({
                    ...site,
                    scheduleCron:
                      site.scheduleCron?.trim() || DEFAULT_WORDPRESS_SCHEDULE,
                  })),
                );
              } else {
                setSiteErrors({});
              }
            }}
            wordpressSites={wordpressSites}
            onWordPressSitesChange={(sites) => {
              setWordpressSites(sites);
              // Clear site errors for any changed sites
              setSiteErrors({});
            }}
            siteErrors={siteErrors}
            reconcileEnabled={reconcileEnabled}
            onReconcileEnabledChange={setReconcileEnabled}
            disabled={isUpdating}
            isEditing
            expandedSiteId={expandedSiteId}
            onExpandedSiteIdChange={setExpandedSiteId}
            showSiteManagement={!hasExistingWpSites}
            disableSourceChange={hasExistingWpSites}
            sourceChangeDisabledReason={
              hasExistingWpSites
                ? 'Remove all WordPress sites to change the collection source. Site configuration is managed on the collection page.'
                : undefined
            }
          />
        </div>
      )}
    </Modal>
  );
};
